import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Search,
  Building2,
  Globe,
  GitBranch,
  Landmark,
  ChevronRight,
  ChevronDown,
  Circle,
} from "lucide-react";
import { useTran } from "@/hooks";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getUnitTypeMeta } from "@/features/organization/utils/organization.helpers";
import OrgStatusTag from "@/features/organization/components/OrgStatusTag";
import { useAuthStore } from "@/features/auth";
import SelectAllButton from "@/components/ui/SelectAllButton";

const SUMMARY_LEVEL_STYLES = [
  { icon: Building2, className: "bg-sky-100 text-sky-700" },
  { icon: Landmark, className: "bg-emerald-100 text-emerald-700" },
  { icon: GitBranch, className: "bg-amber-100 text-amber-700" },
  { icon: Circle, className: "bg-indigo-100 text-indigo-700" },
];

const getLevelNumber = (level) => {
  const match = /^level_(\d+)$/.exec(level || "");
  return match ? Number(match[1]) : null;
};

const sortLevels = (levels = []) =>
  [...levels].sort((a, b) => {
    const aLevel = getLevelNumber(a);
    const bLevel = getLevelNumber(b);

    if (aLevel !== null && bLevel !== null) return aLevel - bLevel;
    if (aLevel !== null) return -1;
    if (bLevel !== null) return 1;

    return String(a).localeCompare(String(b));
  });

const StepSelectOrganizations = ({
  formData,
  updateFormData,
  organizationTree = [],
  companyId,
  usedOrganizations = [],
}) => {
  const { t } = useTran();
  const { isJJSEA } = useAuthStore();
  const [search, setSearch] = useState("");

  const getNodeId = (node) => node?._id || node?.id || node?.key;

  const normalizeOrgNode = (node) => ({
    ...node,
    id: getNodeId(node),
    name: node.name || node.label || node.companyName || "-",
    code: node.code || node.unitCode || "",
    country: node.country || node.countryName || "",
    countryLabel: node.countryLabel || node.countryName || node.country || "",
    unitLevel: node.unitLevel || node.unit_level || node.level,
    children: (node.children || node.organizationUnits || node.units || []).map(
      normalizeOrgNode,
    ),
  });

  const normalizedTree = useMemo(
    () => organizationTree.map(normalizeOrgNode),
    [organizationTree],
  );

  const flattenTree = useCallback((nodes = []) => {
    return nodes.flatMap((node) => [node, ...flattenTree(node.children || [])]);
  }, []);

  const ALL_ORGS = useMemo(
    () => flattenTree(normalizedTree),
    [normalizedTree, flattenTree],
  );

  const getNodeLevel = (node, depth = 0) => {
    const unitLevel = node?.unitLevel || node?.unit_level || node?.level;

    if (unitLevel === "parent_company") return "parent_company";
    if (unitLevel === "business_unit") return "level_1";
    if (unitLevel === "company") return "level_2";
    if (unitLevel === "site") return "level_3";

    if (unitLevel) return unitLevel;

    return `level_${Math.max(depth, 1)}`;
  };

  const getLevelLabelForJJ = (level, t) => {
    if (level === "parent_company") return t("Parent Company");
    if (level === "level_1") return t("Business Unit");
    if (level === "level_2") return t("Company");
    if (level === "level_3") return t("Site");

    const match = /^level_(\d+)$/.exec(level || "");
    if (match) return `${t("Level")} ${match[1]}`;

    return t(level || "Other");
  };

  const getLevelLabel = (level, t) => {
    if (level === "parent_company") return t("Parent Company");
    if (level === "level_1") return t("Level 1");
    if (level === "level_2") return t("Level 2");
    if (level === "level_3") return t("Level 3");

    const match = /^level_(\d+)$/.exec(level || "");
    if (match) return `${t("Level")} ${match[1]}`;

    return t(level || "Other");
  };

  const filterOrgTree = (nodes = [], query = "") => {
    const searchText = query.trim().toLowerCase();
    if (!searchText) return nodes;

    const visit = (node) => {
      const nodeText = [
        node.name,
        node.code,
        node.country,
        node.countryLabel,
        node.type,
        node.unitLevel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const isMatch = nodeText.includes(searchText);
      const children = (node.children || []).map(visit).filter(Boolean);

      if (isMatch) {
        return node;
      }

      if (children.length > 0) {
        return { ...node, children };
      }

      return null;
    };

    return nodes.map(visit).filter(Boolean);
  };

  const filteredTree = useMemo(
    () => filterOrgTree(normalizedTree, search),
    [normalizedTree, search],
  );

  const selectedIds = useMemo(
    () => new Set((formData.organizations || []).map(String)),
    [formData.organizations],
  );

  const [expandedIds, setExpandedIds] = useState(new Set());

  useEffect(() => {
    setExpandedIds(new Set(normalizedTree.map((node) => node.id)));
  }, [normalizedTree]);

  const toggleOrg = (org) => {
    // Select All / Unselect All from Business Unit
    if (org.__selectAllIds) {
      const current = formData.organizations || [];
      const currentIds = current.map(String);

      const relatedIds = org.__selectAllIds.map(String).filter((id) => {
        if (!yearIsCurrentOrFuture) return true;
        const orgData = ALL_ORGS.find((o) => String(o.id) === String(id));
        return orgData?.status !== "inactive";
      });

      const allSelected = relatedIds.every((id) => currentIds.includes(id));

      if (allSelected) {
        // Remove all children
        updateFormData(
          "organizations",
          current.filter((id) => !relatedIds.includes(String(id))),
        );
      } else {
        // Add all children
        updateFormData(
          "organizations",
          Array.from(new Set([...current, ...relatedIds])),
        );
      }

      return;
    }

    const orgId = String(org.id);

    // Don't allow changes for locked organizations
    if (usedOrganizations.some((id) => String(id) === orgId)) {
      return;
    }

    const orgNode = ALL_ORGS.find((o) => String(o.id) === orgId);
    if (yearIsCurrentOrFuture && orgNode?.status === "inactive") {
      return;
    }

    const current = formData.organizations || [];

    if (current.some((id) => String(id) === orgId)) {
      updateFormData(
        "organizations",
        current.filter((id) => String(id) !== orgId),
      );
    } else {
      updateFormData("organizations", [...current, orgId]);
    }
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const selectedYear = Number(formData?.startYear);
  const yearIsCurrentOrFuture =
    Number.isFinite(selectedYear) && selectedYear >= new Date().getFullYear();

  const visibleOrganizations = useMemo(
    () => flattenTree(filteredTree),
    [filteredTree, flattenTree],
  );

  const visibleSelectableIds = useMemo(
    () =>
      visibleOrganizations
        .filter((org) => {
          if (yearIsCurrentOrFuture && org.status === "inactive") return false;
          return true;
        })
        .map((org) => org.id)
        .filter(Boolean)
        .map(String)
        .filter(
          (id) =>
            !usedOrganizations.some((usedId) => String(usedId) === String(id)),
        ),
    [visibleOrganizations, usedOrganizations, yearIsCurrentOrFuture],
  );

  const areAllVisibleSelected =
    visibleSelectableIds.length > 0 &&
    visibleSelectableIds.every((id) => selectedIds.has(String(id)));

  const isSomeVisibleSelected =
    visibleSelectableIds.some((id) => selectedIds.has(String(id))) &&
    !areAllVisibleSelected;

  const toggleAllVisible = () => {
    const current = formData.organizations || [];
    const visibleSet = new Set(visibleSelectableIds.map(String));

    if (areAllVisibleSelected) {
      const next = current.filter((id) => !visibleSet.has(String(id)));

      updateFormData("organizations", next);
      return;
    }

    updateFormData(
      "organizations",
      Array.from(new Set([...current, ...visibleSelectableIds])),
    );
  };

  const selectedOrgDetails = useMemo(() => {
    return (formData.organizations || [])
      .map((id) => ALL_ORGS.find((item) => String(item.id) === String(id)))
      .filter(Boolean);
  }, [formData.organizations, ALL_ORGS]);

  const selectedOrganizationCount = selectedOrgDetails.length;

  const levelCounts = useMemo(() => {
    const counts = {};

    selectedOrgDetails.forEach((org) => {
      const level = getNodeLevel(org);
      counts[level] = (counts[level] || 0) + 1;
    });

    return counts;
  }, [selectedOrgDetails]);

  const levelNameByLevel = useMemo(() => {
    const names = {};

    ALL_ORGS.forEach((org) => {
      const level = getNodeLevel(org);
      if (!level || names[level]) return;
      names[level] = org.unitLevelName || org.unit_level_name || "";
    });

    return names;
  }, [ALL_ORGS]);

  const summaryLevelItems = useMemo(() => {
    const levels = isJJSEA
      ? ["level_1", "level_2", "level_3"]
      : sortLevels(
          Array.from(
            new Set(
              ALL_ORGS.map((org) => getNodeLevel(org)).filter((level) =>
                /^level_\d+$/.test(level || ""),
              ),
            ),
          ),
        );

    return levels.map((level, index) => {
      const style = SUMMARY_LEVEL_STYLES[index % SUMMARY_LEVEL_STYLES.length];
      const levelLabel = isJJSEA
        ? getLevelLabelForJJ(level, t)
        : levelNameByLevel[level] || getLevelLabel(level, t);

      return {
        key: level,
        label: levelLabel,
        value: levelCounts[level] || 0,
        icon: style.icon,
        className: style.className,
      };
    });
  }, [ALL_ORGS, isJJSEA, levelCounts, levelNameByLevel, t]);

  const summaryItems = [
    {
      key: "total",
      label: t("Organizations"),
      value: selectedOrganizationCount,
      icon: Building2,
      className: "bg-primary/10 text-primary",
    },
    ...summaryLevelItems,
  ];

  useEffect(() => {
    const organizations = (formData.organizations || []).map(String);

    const allOrgIds = ALL_ORGS.map((org) => String(org.id));

    const isAllOrganization =
      allOrgIds.length > 0 &&
      allOrgIds.every((id) => organizations.includes(id));

    const isCompanySelected = organizations.includes(String(companyId));

    updateFormData("isAllOrganization", isAllOrganization);
    updateFormData("isCompanySelected", isCompanySelected);
  }, [formData.organizations, ALL_ORGS, companyId]);

  const OrgTreeNode = ({
    node,
    depth = 0,
    selectedIds,
    onToggle,
    expandedIds,
    onToggleExpand,
    usedOrganizations = [],
    yearIsCurrentOrFuture: yearBlocked = false,
  }) => {
    const { t } = useTran();
    const hasChildren = node.children?.length > 0;
    const nodeId = getNodeId(node);
    const isExpanded = expandedIds.has(nodeId);
    const isSelected = selectedIds.has(nodeId);
    const level = getNodeLevel(node, depth);
    const meta = getUnitTypeMeta(level);
    const Icon = meta.icon || Building2;
    const countryLabel = node.countryLabel || node.country;
    const showCountry = level === "level_2" && countryLabel;

    const isLocked = usedOrganizations.some(
      (usedId) => String(usedId) === String(nodeId),
    );
    const isInactiveBlocked = node.status === "inactive" && yearBlocked;

    // Collect all children ids under this business unit
    const getAllRelatedIds = (currentNode) => {
      let ids = [];

      const currentId = getNodeId(currentNode);

      if (currentId) {
        ids.push(String(currentId));
      }

      if (currentNode.children?.length) {
        currentNode.children.forEach((child) => {
          ids = [...ids, ...getAllRelatedIds(child)];
        });
      }

      return ids;
    };

    const relatedIds = level === "level_1" ? getAllRelatedIds(node) : [];
    const lockedIds = usedOrganizations.map(String);
    const inactiveChildIds = new Set(
      yearBlocked
        ? (node.children || []).flatMap((c) => {
            const ids = [];
            const walk = (n) => {
              if (n.status === "inactive") ids.push(String(getNodeId(n)));
              (n.children || []).forEach(walk);
            };
            walk(c);
            return ids;
          })
        : [],
    );
    const selectableRelatedIds = relatedIds.filter(
      (id) =>
        !lockedIds.includes(String(id)) && !inactiveChildIds.has(String(id)),
    );

    const areAllSelected =
      selectableRelatedIds.length > 0 &&
      selectableRelatedIds.every((id) => selectedIds.has(id));

    const isGroupFullyUsed =
      relatedIds.length > 0 && selectableRelatedIds.length === 0;

    const handleSelectAllChange = () => {
      onToggle({
        ...node,
        __selectAllIds: selectableRelatedIds,
      });
    };

    return (
      <>
        <div
          className={cn(
            "group flex min-h-[42px] items-center gap-2 rounded-radius border border-transparent px-2.5 py-1.5 transition-colors hover:bg-muted/60",
            isSelected && "border-primary/20 bg-primary/5",
          )}
          style={{ paddingLeft: `${depth * 22 + 12}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={() => onToggleExpand(nodeId)}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-border bg-background text-muted-foreground transition hover:bg-muted hover:text-foreground"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
            </button>
          ) : (
            <span className="h-5 w-5 shrink-0" />
          )}

          <span
            className={cn(
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted",
              meta.color,
            )}
          >
            <Icon className="h-3.5 w-3.5" />
          </span>

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-2">
              <span className="truncate text-sm font-medium text-foreground">
                {node.name}
              </span>

              {showCountry && (
                <Badge
                  variant="outline"
                  className="shrink-0 rounded-md border-emerald-200 bg-emerald-50 px-1.5 py-0 text-[10px]"
                >
                  <Globe className="mr-1 h-3 w-3" />
                  {countryLabel}
                </Badge>
              )}
            </div>
            <div className="mt-1 flex items-center gap-1.5">
              <span className="text-[10px] leading-tight text-muted-foreground">
                {(isJJSEA ? getLevelLabelForJJ : getLevelLabel)(level, t)}
              </span>
              <OrgStatusTag
                status={node.status}
                inactiveDate={node.inactiveDate}
                className="text-[10px]"
              />
            </div>
          </div>

          {level === "level_1" && relatedIds.length > 0 && (
            <SelectAllButton
              areAllSelected={areAllSelected}
              value={[...selectedIds].map(String)}
              allRelatedIds={selectableRelatedIds}
              onChange={handleSelectAllChange}
              t={t}
              disabled={isGroupFullyUsed}
              className="h-6 px-2 text-[11px]"
            />
          )}

          {!isInactiveBlocked && (
            <Checkbox
              className="shrink-0"
              checked={isSelected}
              disabled={isLocked}
              onCheckedChange={() => onToggle(node)}
            />
          )}
        </div>

        {hasChildren &&
          isExpanded &&
          node.children.map((child) => (
            <OrgTreeNode
              key={getNodeId(child)}
              node={child}
              depth={depth + 1}
              selectedIds={selectedIds}
              onToggle={onToggle}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              usedOrganizations={usedOrganizations}
              yearIsCurrentOrFuture={yearBlocked}
            />
          ))}
      </>
    );
  };

  return (
    <div className="rounded-radius border border-border bg-card">
      {/* Section Header */}
      <div className="border-b border-border px-6 py-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-radius bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="font-bold text-primary">
              {t("Select Organizations")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t(
                "Choose the organizations that will be included in the reporting scope.",
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1fr)_220px]">
        <div className="rounded-radius border border-border bg-card shadow-sm">
          <div className="space-y-4 border-b border-border bg-muted/20 px-4 py-2">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground">
                  {t("Organization Tree")}
                </p>
                {/* <p className="mt-1 text-xs text-muted-foreground">
                  {t(
                    "Filter, navigate, and select organizations from one place.",
                  )}
                </p> */}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>
                  {selectedOrganizationCount}/{ALL_ORGS.length} {t("selected")}
                </span>
              </div>
            </div>

            {/* <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="rounded-md bg-background">
                {visibleOrganizations.length} {t("visible")}
              </Badge>
              <Badge variant="outline" className="rounded-md bg-background">
                {(formData.organizations || []).length} {t("selected")}
              </Badge>
            </div> */}
          </div>

          <div className="sticky top-0 z-10 grid gap-3 border-b border-border bg-card px-4 py-3 lg:grid-cols-[minmax(280px,1fr)_auto] lg:items-center">
            <div className="relative lg:max-w-[600px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("Search by organization or country ...")}
                className="h-10 bg-background pl-9"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 lg:justify-end">
              {/* <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>
                  {selectedVisibleCount}/{visibleSelectableIds.length}{" "}
                  {t("selected")}
                </span>
              </div> */}
              <label className="flex min-w-0 items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {areAllVisibleSelected ? t("UnSelect All") : t("Select All")}
                </span>
                <Checkbox
                  checked={
                    areAllVisibleSelected
                      ? true
                      : isSomeVisibleSelected
                        ? ""
                        : false
                  }
                  disabled={visibleSelectableIds.length === 0}
                  onCheckedChange={toggleAllVisible}
                />
              </label>
            </div>
          </div>

          <div className="p-2">
            <div className="max-h-[calc(100vh-430px)] min-h-[320px] space-y-0.5 overflow-y-auto pr-1">
              {filteredTree.length === 0 ? (
                <div className="rounded-radius border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
                  {t("No organizations found.")}
                </div>
              ) : (
                filteredTree.map((node) => (
                  <OrgTreeNode
                    key={getNodeId(node)}
                    node={node}
                    depth={0}
                    selectedIds={selectedIds}
                    onToggle={toggleOrg}
                    expandedIds={expandedIds}
                    onToggleExpand={toggleExpand}
                    usedOrganizations={usedOrganizations}
                    yearIsCurrentOrFuture={yearIsCurrentOrFuture}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <aside className="rounded-radius border border-border bg-muted/20 p-4 xl:self-start">
          <p className="mb-3 text-xs font-medium text-muted-foreground">
            {t("Selection Summary")}
          </p>
          <div className="grid grid-cols-2 gap-2 xl:grid-cols-1">
            {summaryItems.map((item) => {
              const Icon = item.icon;

              return (
                <div
                  key={item.key}
                  className="flex items-center gap-2 rounded-radius border border-border bg-card px-4 py-4"
                >
                  <div
                    className={cn(
                      "flex h-10 w-10 shrink-0 items-center justify-center rounded-radius",
                      item.className,
                    )}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-bold leading-none">
                      {item.value}
                    </p>
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {item.label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </div>
  );
};

export default StepSelectOrganizations;
